import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { playSoundEffect } from './hooks/useSoundEffects';
import {
  EXPLORE_UNKNOWN_POSTCARD_MISSION_COUNT,
  getExploreUnknownPostcardImageUrl,
  resolveExploreUnknownPostcardPreviewMissionIndex,
} from './config/exploreUnknownPostcards';
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from './exploreUnknownPortalLayout';
import './ExploreUnknownPostcards.css';

/**
 * @param {string} baseClass
 * @param {number} missionIndex
 * @param {Set<number>} bossMissionSet
 */
function buildPostcardSlotBtnClass(baseClass, missionIndex, bossMissionSet) {
  if (bossMissionSet.has(missionIndex)) {
    return `${baseClass} eu-postcards-slot-btn--boss`;
  }
  return baseClass;
}

/**
 * Lower-right portal control + postcard collection modal (account-wide earned indices).
 *
 * @param {{ earnedMissionIndices: number[], isMobile?: boolean }} props
 */
export default function ExploreUnknownPostcards({ earnedMissionIndices, isMobile = false }) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [enlargedMissionIndex, setEnlargedMissionIndex] = useState(null);

  const earnedSet = useMemo(() => new Set(earnedMissionIndices), [earnedMissionIndices]);

  const bossMissionSet = useMemo(
    () => new Set(EXPLORE_UNKNOWN_PORTAL_LAYOUT.bossMissionIndices),
    []
  );

  const earnedViewableIndices = useMemo(
    () =>
      [...earnedMissionIndices]
        .filter((idx) => Number.isInteger(idx) && getExploreUnknownPostcardImageUrl(idx) != null)
        .sort((a, b) => a - b),
    [earnedMissionIndices]
  );

  const previewUrl = useMemo(() => {
    const idx = resolveExploreUnknownPostcardPreviewMissionIndex(earnedMissionIndices);
    const url = getExploreUnknownPostcardImageUrl(idx);
    if (!url) {
      throw new Error(
        `ExploreUnknownPostcards: preview resolver returned mission ${idx} without a postcard image`
      );
    }
    return url;
  }, [earnedMissionIndices]);

  const missionSlots = useMemo(() => {
    return Array.from({ length: EXPLORE_UNKNOWN_POSTCARD_MISSION_COUNT }, (_, i) => i + 1);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEnlargedMissionIndex(null);
  }, []);

  const openModal = useCallback(() => {
    playSoundEffect('/sounds/click.mp3');
    setModalOpen(true);
  }, []);

  const navigateEnlargedPostcard = useCallback(
    (direction) => {
      if (enlargedMissionIndex == null) {
        return;
      }
      const currentPosition = earnedViewableIndices.indexOf(enlargedMissionIndex);
      if (currentPosition < 0) {
        throw new Error(
          `ExploreUnknownPostcards: enlarged mission ${enlargedMissionIndex} is not in earnedViewableIndices`
        );
      }
      const nextPosition = currentPosition + direction;
      if (nextPosition < 0 || nextPosition >= earnedViewableIndices.length) {
        return;
      }
      setEnlargedMissionIndex(earnedViewableIndices[nextPosition]);
    },
    [enlargedMissionIndex, earnedViewableIndices]
  );

  useEffect(() => {
    if (!modalOpen && enlargedMissionIndex == null) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (enlargedMissionIndex != null) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          navigateEnlargedPostcard(-1);
          return;
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          navigateEnlargedPostcard(1);
          return;
        }
      }
      if (event.key === 'Escape') {
        if (enlargedMissionIndex != null) {
          setEnlargedMissionIndex(null);
        } else {
          closeModal();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen, enlargedMissionIndex, closeModal, navigateEnlargedPostcard]);

  const earnedCount = earnedMissionIndices.length;

  const enlargedViewablePosition =
    enlargedMissionIndex == null ? -1 : earnedViewableIndices.indexOf(enlargedMissionIndex);
  const canGoPrev = enlargedViewablePosition > 0;
  const canGoNext =
    enlargedViewablePosition >= 0 && enlargedViewablePosition < earnedViewableIndices.length - 1;
  const showLightboxNav = earnedViewableIndices.length > 1 && enlargedViewablePosition >= 0;

  const mobileOverlayClass = isMobile ? ' eu-postcards--mobile' : '';

  return (
    <>
      <div className="eu-postcards-entry">
        <button
          type="button"
          className="eu-postcards-entry-btn"
          onClick={(event) => {
            event.stopPropagation();
            openModal();
          }}
          aria-haspopup="dialog"
          aria-expanded={modalOpen}
          aria-label={t('exploreUnknownPage.postcards.openCollection', { earned: earnedCount, total: EXPLORE_UNKNOWN_POSTCARD_MISSION_COUNT })}
        >
          <span className="eu-postcards-entry-stack" aria-hidden="true">
            <span className="eu-postcards-entry-card eu-postcards-entry-card--back" />
            <img
              className="eu-postcards-entry-card eu-postcards-entry-card--front"
              src={previewUrl}
              alt=""
              draggable={false}
            />
          </span>
          <span className="eu-postcards-entry-label">{t('exploreUnknownPage.postcards.entryLabel')}</span>
        </button>
      </div>

      {modalOpen
        ? createPortal(
            <div
              className={`eu-postcards-modal-overlay${mobileOverlayClass}`}
              role="presentation"
              onClick={closeModal}
            >
              <div
                className="eu-postcards-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="eu-postcards-modal-title"
                onClick={(event) => event.stopPropagation()}
              >
                <header className="eu-postcards-modal-header">
                  <div>
                    <h2 id="eu-postcards-modal-title" className="eu-postcards-modal-title">
                      {t('exploreUnknownPage.postcards.modalTitle')}
                    </h2>
                    <p className="eu-postcards-modal-count">
                      {t('exploreUnknownPage.postcards.collectedCount', {
                        earned: earnedCount,
                        total: EXPLORE_UNKNOWN_POSTCARD_MISSION_COUNT,
                      })}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="eu-postcards-modal-close"
                    onClick={closeModal}
                    aria-label={t('common.close')}
                  >
                    ×
                  </button>
                </header>
                <div className="eu-postcards-modal-grid-wrap">
                  <div className="eu-postcards-modal-grid">
                    {missionSlots.map((missionIndex) => {
                      const earned = earnedSet.has(missionIndex);
                      const imageUrl = getExploreUnknownPostcardImageUrl(missionIndex);
                      const isBoss = bossMissionSet.has(missionIndex);
                      const slotClassName = isBoss ? 'eu-postcards-slot eu-postcards-slot--boss' : 'eu-postcards-slot';
                      if (!earned) {
                        return (
                          <div key={missionIndex} className={slotClassName}>
                            <div
                              className={buildPostcardSlotBtnClass(
                                'eu-postcards-slot-btn eu-postcards-slot-btn--locked',
                                missionIndex,
                                bossMissionSet
                              )}
                              aria-label={t('exploreUnknownPage.postcards.lockedSlot', { missionIndex })}
                            >
                              <span className="eu-postcards-slot-question" aria-hidden="true">
                                ?
                              </span>
                            </div>
                            <span className="eu-postcards-slot-index">{missionIndex}</span>
                          </div>
                        );
                      }
                      if (!imageUrl) {
                        return (
                          <div key={missionIndex} className={slotClassName}>
                            <div
                              className={buildPostcardSlotBtnClass(
                                'eu-postcards-slot-btn eu-postcards-slot-btn--earned-pending',
                                missionIndex,
                                bossMissionSet
                              )}
                              aria-label={t('exploreUnknownPage.postcards.earnedPendingArt', { missionIndex })}
                            >
                              <span className="eu-postcards-slot-pending-label">
                                {t('exploreUnknownPage.postcards.earnedPendingShort')}
                              </span>
                            </div>
                            <span className="eu-postcards-slot-index">{missionIndex}</span>
                          </div>
                        );
                      }
                      return (
                        <div key={missionIndex} className={slotClassName}>
                          <button
                            type="button"
                            className={buildPostcardSlotBtnClass(
                              'eu-postcards-slot-btn eu-postcards-slot-btn--earned',
                              missionIndex,
                              bossMissionSet
                            )}
                            onClick={() => {
                              playSoundEffect('/sounds/click.mp3');
                              setEnlargedMissionIndex(missionIndex);
                            }}
                            aria-label={t('exploreUnknownPage.postcards.viewPostcard', { missionIndex })}
                          >
                            <img
                              className="eu-postcards-slot-img"
                              src={imageUrl}
                              alt=""
                              draggable={false}
                            />
                          </button>
                          <span className="eu-postcards-slot-index">{missionIndex}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {enlargedMissionIndex != null && getExploreUnknownPostcardImageUrl(enlargedMissionIndex)
        ? createPortal(
            <div
              className={`eu-postcards-lightbox-overlay${mobileOverlayClass}`}
              role="dialog"
              aria-modal="true"
              aria-label={t('exploreUnknownPage.postcards.enlargedAlt', { missionIndex: enlargedMissionIndex })}
              onClick={() => setEnlargedMissionIndex(null)}
            >
              <button
                type="button"
                className="eu-postcards-lightbox-close"
                onClick={() => setEnlargedMissionIndex(null)}
                aria-label={t('common.close')}
              >
                ×
              </button>
              <div
                className="eu-postcards-lightbox-stage"
                onClick={(event) => event.stopPropagation()}
              >
                {showLightboxNav ? (
                  <button
                    type="button"
                    className="eu-postcards-lightbox-nav eu-postcards-lightbox-nav--prev"
                    disabled={!canGoPrev}
                    onClick={() => {
                      playSoundEffect('/sounds/click.mp3');
                      navigateEnlargedPostcard(-1);
                    }}
                    aria-label={t('exploreUnknownPage.postcards.previousPostcard')}
                  >
                    <FaChevronLeft aria-hidden="true" />
                  </button>
                ) : null}
                <img
                  className="eu-postcards-lightbox-img"
                  src={getExploreUnknownPostcardImageUrl(enlargedMissionIndex)}
                  alt={t('exploreUnknownPage.postcards.enlargedAlt', { missionIndex: enlargedMissionIndex })}
                  draggable={false}
                />
                {showLightboxNav ? (
                  <button
                    type="button"
                    className="eu-postcards-lightbox-nav eu-postcards-lightbox-nav--next"
                    disabled={!canGoNext}
                    onClick={() => {
                      playSoundEffect('/sounds/click.mp3');
                      navigateEnlargedPostcard(1);
                    }}
                    aria-label={t('exploreUnknownPage.postcards.nextPostcard')}
                  >
                    <FaChevronRight aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
